<script setup>
import { ref, onMounted } from 'vue';

// Instagram API-Integration entfernt
const isLoading = ref(false);
const error = ref(null);
const successMessage = ref('');

// Manuelle Foto-Upload-Funktionalität (für Fälle ohne Instagram-Integration)
const manualPhotos = ref([]);
const newPhotoData = ref({
  caption: '',
  date: new Date().toISOString().slice(0, 10),
  isPublished: true
});
const selectedFile = ref(null);
const imagePreview = ref('');
const isEditMode = ref(false);
const editingPhotoId = ref(null);
const showForm = ref(false);

// Lade gespeicherte Fotos
const fetchManualPhotos = async () => {
  isLoading.value = true;
  error.value = null;
  
  try {
    // Echte API-Anfrage verwenden statt localStorage
    const response = await fetch('/api/admin_photos.php');
    if (!response.ok) throw new Error('Fehler beim Laden der Fotos');
    const data = await response.json();
    manualPhotos.value = data.photos || [];
  } catch (err) {
    console.error('Fehler beim Laden der Fotos:', err);
    error.value = err.message || 'Fehler beim Laden der Fotos.';
  } finally {
    isLoading.value = false;
  }
};

// Formular für ein neues Foto anzeigen
const showAddPhotoForm = () => {
  showForm.value = true;
  isEditMode.value = false;
  newPhotoData.value = {
    caption: '',
    date: new Date().toISOString().slice(0, 10),
    isPublished: true
  };
  selectedFile.value = null;
  imagePreview.value = '';
};

// Formular abbrechen
const cancelForm = () => {
  showForm.value = false;
  selectedFile.value = null;
  imagePreview.value = '';
};

// Foto-Bearbeitung starten
const editPhoto = (photo) => {
  isEditMode.value = true;
  editingPhotoId.value = photo.id;
  newPhotoData.value = { ...photo };
  imagePreview.value = photo.imageUrl;
  showForm.value = true;
};

// Handle file selection
const handleFileChange = (event) => {
  const file = event.target.files[0];
  selectedFile.value = file;
  
  if (file) {
    // Erzeugen einer URL für die Bildvorschau
    imagePreview.value = URL.createObjectURL(file);
  } else {
    imagePreview.value = '';
  }
};

// Foto speichern (neu oder bearbeitet)
const savePhoto = async () => {
  isLoading.value = true;
  error.value = null;
  successMessage.value = '';
  
  try {
    // Validierung
    if (!newPhotoData.value.caption.trim()) {
      throw new Error('Bitte gib eine Bildunterschrift ein');
    }
    
    if (!isEditMode.value && !selectedFile.value) {
      throw new Error('Bitte wähle ein Bild aus');
    }
    
    if (isEditMode.value) {
      // Foto aktualisieren
      let formData = new FormData();
      
      if (selectedFile.value) {
        // Wenn ein neues Bild ausgewählt wurde, hochladen
        formData.append('image', selectedFile.value);
        formData.append('caption', newPhotoData.value.caption);
        formData.append('date', newPhotoData.value.date);
        formData.append('isPublished', newPhotoData.value.isPublished ? '1' : '0');
        
        // Bild hochladen
        const uploadResponse = await fetch('/api/upload.php', {
          method: 'POST',
          body: formData
        });
        
        const uploadData = await uploadResponse.json();
        
        if (!uploadData.success) {
          throw new Error(uploadData.error || 'Fehler beim Hochladen des Bilds');
        }
        
        // Verwende die URL des hochgeladenen Bilds für die Aktualisierung
        newPhotoData.value.imageUrl = uploadData.imageUrl;
      }
      
      // Foto aktualisieren
      const response = await fetch(`/api/admin_photos.php?id=${editingPhotoId.value}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newPhotoData.value)
      });
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Fehler beim Aktualisieren des Fotos');
      }
      
      successMessage.value = 'Foto erfolgreich aktualisiert!';
    } else {
      // Neues Foto hochladen
      const formData = new FormData();
      formData.append('image', selectedFile.value);
      formData.append('caption', newPhotoData.value.caption);
      formData.append('date', newPhotoData.value.date);
      formData.append('isPublished', newPhotoData.value.isPublished ? '1' : '0');
      
      const response = await fetch('/api/upload.php', {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Fehler beim Hochladen des Fotos');
      }
      
      successMessage.value = 'Neues Foto erfolgreich hochgeladen!';
    }
    
    // Fotos neu laden und Formular zurücksetzen
    await fetchManualPhotos();
    showForm.value = false;
    selectedFile.value = null;
    imagePreview.value = '';
  } catch (err) {
    console.error('Fehler beim Speichern des Fotos:', err);
    error.value = err.message || 'Fehler beim Speichern des Fotos.';
  } finally {
    isLoading.value = false;
  }
};

// Foto löschen
const deletePhoto = async (id) => {
  if (!confirm('Bist du sicher, dass du dieses Foto löschen möchtest?')) {
    return;
  }
  
  isLoading.value = true;
  error.value = null;
  
  try {
    // Tatsächlichen API-Endpunkt verwenden
    const response = await fetch(`/api/admin_photos.php?id=${id}`, {
      method: 'DELETE'
    });
    
    if (!response.ok) throw new Error('Fehler beim Löschen des Fotos');
    
    await fetchManualPhotos(); // Nach dem Löschen die Fotos neu laden
    successMessage.value = 'Foto erfolgreich gelöscht!';
  } catch (err) {
    console.error('Fehler beim Löschen des Fotos:', err);
    error.value = err.message || 'Fehler beim Löschen des Fotos.';
  } finally {
    isLoading.value = false;
  }
};

// Veröffentlichungsstatus ändern
const togglePublishStatus = async (photo) => {
  isLoading.value = true;
  error.value = null;
  
  try {
    // Tatsächlichen API-Endpunkt zum Ändern des Status verwenden
    const response = await fetch(`/api/admin_photos.php?id=${photo.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ isPublished: !photo.isPublished })
    });
    
    if (!response.ok) throw new Error('Fehler beim Ändern des Status');
    
    await fetchManualPhotos(); // Nach der Änderung die Fotos neu laden
    successMessage.value = `Foto wurde ${!photo.isPublished ? 'veröffentlicht' : 'zurückgezogen'}!`;
    setTimeout(() => {
      successMessage.value = '';
    }, 3000);
  } catch (err) {
    console.error('Fehler beim Ändern des Veröffentlichungsstatus:', err);
    error.value = err.message || 'Fehler beim Ändern des Status.';
  } finally {
    isLoading.value = false;
  }
};

// Prüfen, ob ein Instagram-Token vorhanden ist
onMounted(() => {
  const savedToken = localStorage.getItem('instagram_token_demo');
  if (savedToken) {
    instagramToken.value = savedToken;
    isTokenSaved.value = true;
  }
  
  fetchManualPhotos();
});
</script>

<template>
  <div class="photos-manager">
    <h2 class="manager-title">Fotos verwalten</h2>
    
    <div class="management-sections">
      <!-- Instagram Integration Section -->
      <div class="section instagram-section">
        <h3>Instagram-Integration</h3>
        <p class="section-description">
          Verbinde deinen Instagram-Account, um Fotos automatisch auf der Website anzuzeigen.
          Alle Fotos, die du auf Instagram postest, werden auf der Website erscheinen.
        </p>
        
        <div class="instagram-form">
          <div class="input-group">
            <label for="instagram-token">Instagram API Access Token</label>
            <input 
              type="text" 
              id="instagram-token" 
              v-model="instagramToken" 
              placeholder="Gib deinen Instagram-Token ein"
              :disabled="isLoading"
            >
            <p class="helper-text">
              <a href="https://developers.facebook.com/docs/instagram-basic-display-api/getting-started" target="_blank">
                Wie erhalte ich einen Instagram-Token?
              </a>
            </p>
          </div>
          
          <div class="button-group">
            <button 
              @click="generateInstagramToken" 
              class="btn btn-secondary"
              :disabled="isLoading"
            >
              Mit Instagram verbinden
            </button>
            
            <button 
              @click="saveInstagramToken" 
              class="btn btn-primary"
              :disabled="isLoading"
            >
              Token speichern
            </button>
          </div>
          
          <div v-if="isTokenSaved" class="saved-token-info">
            <p>
              <span class="success-icon">✓</span>
              Instagram ist verbunden! Die Fotos werden automatisch auf der Website angezeigt.
            </p>
          </div>
        </div>
      </div>
      
      <!-- Manual Photo Management Section -->
      <div class="section photos-section">
        <h3>Manuelle Fotoverwaltung</h3>
        <p class="section-description">
          Hier kannst du Fotos manuell hochladen und verwalten, falls du keine Instagram-Integration nutzen möchtest.
        </p>
        
        <div class="action-bar">
          <button 
            @click="showAddPhotoForm" 
            class="btn btn-primary"
            :disabled="isLoading || showForm"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <circle cx="8.5" cy="8.5" r="1.5"></circle>
              <polyline points="21 15 16 10 5 21"></polyline>
              <line x1="16" y1="5" x2="16" y2="11"></line>
              <line x1="13" y1="8" x2="19" y2="8"></line>
            </svg>
            Neues Foto hochladen
          </button>
        </div>
        
        <!-- Photo Form -->
        <div v-if="showForm" class="photo-form">
          <h4>{{ isEditMode ? 'Foto bearbeiten' : 'Neues Foto hochladen' }}</h4>
          
          <div class="form-group">
            <label for="photo-caption">Bildunterschrift*</label>
            <input 
              type="text" 
              id="photo-caption" 
              v-model="newPhotoData.caption" 
              required
              placeholder="Beschreibe das Foto"
              class="form-control"
              :disabled="isLoading"
            >
          </div>
          
          <div class="form-group">
            <label for="photo-file">Bild {{ isEditMode ? '(optional)' : '*' }}</label>
            <input 
              type="file" 
              id="photo-file" 
              accept="image/*"
              @change="handleFileChange"
              class="form-control"
              :disabled="isLoading"
            >
            <p v-if="isEditMode" class="helper-text">
              Lass dieses Feld leer, um das aktuelle Bild beizubehalten.
            </p>
          </div>
          
          <div class="form-group">
            <label for="photo-date">Datum</label>
            <input 
              type="date" 
              id="photo-date" 
              v-model="newPhotoData.date" 
              class="form-control"
              :disabled="isLoading"
            >
          </div>
          
          <div class="form-group checkbox-group">
            <label class="checkbox-label">
              <input 
                type="checkbox" 
                v-model="newPhotoData.isPublished" 
                :disabled="isLoading"
              >
              <span>Veröffentlichen</span>
            </label>
          </div>
          
          <div class="form-actions">
            <button 
              @click="cancelForm" 
              class="btn btn-text"
              :disabled="isLoading"
            >
              Abbrechen
            </button>
            
            <button 
              @click="savePhoto" 
              class="btn btn-primary"
              :disabled="isLoading"
            >
              {{ isEditMode ? 'Aktualisieren' : 'Hochladen' }}
            </button>
          </div>
        </div>
        
        <!-- Success/Error Messages -->
        <div v-if="successMessage" class="success-message">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          <span>{{ successMessage }}</span>
        </div>
        
        <div v-if="error" class="error-message">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>{{ error }}</span>
        </div>
        
        <!-- Loading Indicator -->
        <div v-if="isLoading" class="loading-state">
          <div class="spinner"></div>
          <p>Wird geladen...</p>
        </div>
        
        <!-- Photos List -->
        <div v-else-if="manualPhotos.length === 0 && !isLoading" class="empty-state">
          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <circle cx="8.5" cy="8.5" r="1.5"></circle>
            <polyline points="21 15 16 10 5 21"></polyline>
          </svg>
          <p>Noch keine Fotos vorhanden. Lade dein erstes Foto hoch!</p>
        </div>
        
        <div v-else class="photos-list">
          <div 
            v-for="photo in manualPhotos" 
            :key="photo.id" 
            class="photo-item"
            :class="{'photo-unpublished': !photo.isPublished}"
          >
            <div class="photo-preview">
              <img :src="photo.imageUrl" :alt="photo.caption">
              <div v-if="!photo.isPublished" class="unpublished-badge">
                Nicht veröffentlicht
              </div>
            </div>
            
            <div class="photo-details">
              <h4>{{ photo.caption }}</h4>
              <p class="photo-date">
                {{ new Date(photo.date).toLocaleDateString('de-DE', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                }) }}
              </p>
            </div>
            
            <div class="photo-actions">
              <button 
                @click="togglePublishStatus(photo)" 
                class="btn btn-icon"
                :title="photo.isPublished ? 'Veröffentlichung zurückziehen' : 'Veröffentlichen'"
              >
                <span v-if="photo.isPublished">🔒</span>
                <span v-else>🔓</span>
              </button>
              
              <button 
                @click="editPhoto(photo)" 
                class="btn btn-icon"
                title="Bearbeiten"
              >
                ✏️
              </button>
              
              <button 
                @click="deletePhoto(photo.id)" 
                class="btn btn-icon btn-danger"
                title="Löschen"
              >
                🗑️
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.photos-manager {
  padding: 2rem;
}

.manager-title {
  font-size: 1.75rem;
  margin-bottom: 2rem;
  color: var(--primary-color);
}

.management-sections {
  display: flex;
  flex-direction: column;
  gap: 3rem;
}

.section {
  background-color: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.section h3 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: var(--primary-color);
}

.section-description {
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.5;
}

.instagram-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

label {
  font-weight: 500;
  font-size: 0.9rem;
  color: #555;
}

input[type="text"],
input[type="date"] {
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
}

input[type="text"]:focus,
input[type="date"]:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(0, 132, 61, 0.1);
}

.helper-text {
  font-size: 0.8rem;
  color: #777;
  margin-top: 0.25rem;
}

.helper-text a {
  color: var(--primary-color);
  text-decoration: none;
}

.helper-text a:hover {
  text-decoration: underline;
}

.button-group {
  display: flex;
  gap: 1rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background-color: var(--primary-color);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #00733d;
}

.btn-secondary {
  background-color: #f0f0f0;
  color: #333;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #e0e0e0;
}

.btn-text {
  background-color: transparent;
  color: #666;
  padding: 0.75rem 1rem;
}

.btn-text:hover:not(:disabled) {
  background-color: rgba(0, 0, 0, 0.05);
}

.btn-icon {
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.25rem;
  transition: opacity 0.2s ease;
}

.btn-icon:hover {
  opacity: 0.7;
}

.btn-danger {
  color: #e53935;
}

.saved-token-info {
  background-color: rgba(0, 132, 61, 0.1);
  padding: 1rem;
  border-radius: 4px;
  color: var(--primary-color);
}

.success-icon {
  display: inline-block;
  width: 20px;
  height: 20px;
  line-height: 20px;
  text-align: center;
  background-color: var(--primary-color);
  color: white;
  border-radius: 50%;
  margin-right: 0.5rem;
}

.action-bar {
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: flex-end;
}

.photo-form {
  background-color: #f9f9f9;
  padding: 1.5rem;
  border-radius: 6px;
  border: 1px solid #ddd;
  margin-bottom: 2rem;
}

.photo-form h4 {
  margin-bottom: 1.5rem;
  font-size: 1.2rem;
  color: #333;
}

.form-group {
  margin-bottom: 1.5rem;
}

.checkbox-group {
  display: flex;
  align-items: center;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
}

.success-message {
  background-color: rgba(0, 132, 61, 0.1);
  color: var(--primary-color);
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
}

.error-message {
  background-color: #ffebee;
  color: #e53935;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
}

.spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(0, 132, 61, 0.1);
  border-left-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: #777;
  background-color: #f9f9f9;
  border-radius: 6px;
}

.photos-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
}

.photo-item {
  background-color: white;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease;
}

.photo-item:hover {
  transform: translateY(-5px);
}

.photo-unpublished {
  opacity: 0.7;
}

.photo-preview {
  position: relative;
  width: 100%;
  height: 200px;
}

.photo-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.unpublished-badge {
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 0.8rem;
  padding: 0.25rem 0.75rem;
  border-radius: 100px;
}

.photo-details {
  padding: 1rem;
}

.photo-details h4 {
  margin: 0 0 0.5rem;
  font-size: 1rem;
}

.photo-date {
  color: #777;
  font-size: 0.85rem;
  margin: 0;
}

.photo-actions {
  display: flex;
  justify-content: flex-end;
  padding: 0.5rem 1rem 1rem;
  gap: 0.75rem;
}

@media (max-width: 768px) {
  .photos-list {
    grid-template-columns: 1fr;
  }
  
  .button-group {
    flex-direction: column;
  }
}
</style> 